import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageHero from '@/components/feature/PageHero';
import Reveal from '@/components/base/Reveal';
import { caseDetails } from '@/mocks/case-details';

function NotFoundBlock() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="w-full px-4 md:px-6 pt-32 pb-24">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-2xl text-foreground-500"></i>
          </div>
          <h1 className="mt-6 font-heading font-bold text-2xl text-foreground-950">未找到该客户案例</h1>
          <p className="mt-3 text-sm text-foreground-600">您访问的案例可能已调整或暂未公开。</p>
          <Link
            to="/cases"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
          >
            返回客户案例 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default function CaseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const data = caseDetails.find((item) => item.slug === slug);

  if (!data) return <NotFoundBlock />;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <SEO
        title={`${data.name}｜客户案例｜华辉数智`}
        description={data.subtitle}
        keywords={`${data.name},客户案例,${data.tag},华辉数智`}
        path={`/cases/${data.slug}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: data.name,
          description: data.subtitle,
          articleSection: '客户案例',
        }}
      />
      <PageHero
        eyebrow={data.eyebrow}
        title={data.title}
        titleHighlight={data.titleHighlight}
        subtitle={data.subtitle}
        desc={data.desc}
        imageUrl={data.heroImage}
        imageAlt={data.heroAlt}
      />
      <Breadcrumb items={[{ label: '客户案例', href: '/cases' }, { label: data.name }]} />

      <main>
        {/* 案例概览 */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-14 items-start">
              <Reveal>
                <div>
                  <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 leading-snug">
                    案例概览
                  </h2>
                  <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{data.overview}</p>
                </div>
              </Reveal>
              <Reveal>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {data.highlights.map((h) => (
                    <div key={h.title} className="p-5 rounded-lg bg-background-50 border border-background-200">
                      <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                        <i className={`${h.icon} text-xl`}></i>
                      </div>
                      <h3 className="mt-3 text-sm font-semibold text-foreground-950">{h.title}</h3>
                      <p className="mt-1.5 text-xs md:text-sm text-foreground-600 leading-relaxed">{h.desc}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* 背景 / 需求 / 方案 */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { icon: 'ri-history-line', label: '项目背景', text: data.background },
                { icon: 'ri-question-line', label: '客户需求', text: data.demand },
                { icon: 'ri-lightbulb-line', label: '实施方案', text: data.plan },
              ].map((block) => (
                <Reveal key={block.label}>
                  <div className="h-full p-6 rounded-lg bg-background-100 border border-background-200">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                        <i className={`${block.icon} text-lg`}></i>
                      </span>
                      <h3 className="text-base font-bold text-foreground-950">{block.label}</h3>
                    </div>
                    <p className="mt-3 text-sm text-foreground-700 leading-relaxed">{block.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* 使用的产品和技术 */}
            <Reveal className="mt-8">
              <div className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200">
                <h3 className="text-sm font-semibold tracking-widest text-foreground-500 uppercase">
                  使用的产品和技术
                </h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {data.tech.map((t) => (
                    <span key={t} className="px-3 py-1 text-sm rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* 路内停车整体解决方案 */}
        {data.solutionDiagram && (
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <Reveal>
                <div className="max-w-3xl">
                  <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 leading-snug">
                    {data.solutionDiagram.title}
                  </h2>
                  <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    {data.solutionDiagram.desc}
                  </p>
                </div>
              </Reveal>
              <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
                {data.solutionDiagram.images.map((img) => (
                  <Reveal key={img.src}>
                    <figure className="h-full flex flex-col rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                      <div className="w-full bg-background-50 p-4 md:p-6 flex items-center justify-center">
                        <img
                          src={img.src}
                          alt={img.alt}
                          title={`${data.name} ${img.caption}`}
                          className="w-full h-auto max-h-[520px] object-contain"
                        />
                      </div>
                      <figcaption className="mt-auto px-5 py-4 border-t border-background-200 bg-background-50">
                        <div className="text-sm font-semibold text-foreground-950">{img.caption}</div>
                        <div className="mt-1 text-xs text-foreground-500">{img.note}</div>
                      </figcaption>
                    </figure>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 落地实践 */}
        {data.practice && (
          <section className="w-full px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-7xl mx-auto">
              <Reveal>
                <div className="max-w-3xl">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-semibold tracking-wide whitespace-nowrap">
                    <i className="ri-map-pin-2-line"></i>
                    落地实践
                  </span>
                  <h2 className="mt-4 font-heading font-bold text-xl md:text-2xl text-foreground-950 leading-snug">
                    {data.practice.title}
                  </h2>
                  <div className="mt-4 space-y-3">
                    {data.practice.paragraphs.map((p) => (
                      <p key={p} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                        {p}
                      </p>
                    ))}
                  </div>
                </div>
              </Reveal>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {data.practice.images.map((img) => (
                  <Reveal key={img.src}>
                    <figure className="h-full flex flex-col rounded-lg bg-background-100 border border-background-200 overflow-hidden">
                      <div className="w-full aspect-[16/10] bg-background-200/60">
                        <img
                          src={img.src}
                          alt={img.alt}
                          title={`${data.name} ${img.caption}`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <figcaption className="mt-auto px-4 py-3 border-t border-background-200 bg-background-50 text-xs text-foreground-600">
                        {img.caption}
                      </figcaption>
                    </figure>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 可复制经验 + 相关解决方案 */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Reveal>
              <div className="h-full p-6 md:p-8 rounded-lg bg-secondary-100/70 border border-secondary-200">
                <div className="flex items-center gap-2">
                  <i className="ri-lightbulb-flash-line text-secondary-700"></i>
                  <span className="text-sm font-semibold text-secondary-900">可复制经验</span>
                </div>
                <p className="mt-2 text-sm text-foreground-700 leading-relaxed">{data.copyable}</p>
              </div>
            </Reveal>

            <Reveal>
              <div className="h-full p-6 md:p-8 rounded-lg bg-background-50 border border-background-200">
                <h3 className="text-sm font-semibold tracking-widest text-foreground-500 uppercase">相关解决方案</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {data.solutions.map((s) => (
                    <Link
                      key={s.label}
                      to={s.href}
                      className="px-4 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 whitespace-nowrap cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}